import React from 'react';
import { useQueryClient } from 'react-query';
import {
  useGetUserByEmail,
  useGetUserByUid,
  useSetAdmin,
  useThemeRoles,
} from '@olo-web/domain/firebaseAuth';
import { useError } from '@olo-web/utils/common/hooks';
import { auth, FieldValue } from '@olo-web/utils/google/firebase';
import {
  Flex,
  HStack,
  Skeleton,
  VStack,
  Text,
  Button,
  Avatar,
  Tag,
  ButtonGroup,
} from '@chakra-ui/react';
import { Banner } from '@olo-web/components/atoms/Banner';
import admin from '@olo-web/utils/google/firebase/admin';
import { useCustomThemeDraftState } from '@olo-web/client-state';
import { ECustomThemeViews } from '@olo-web/types/enums';
import { useMerchantTheme } from '@domain/merchants/queries/useMerchantTheme';
import { useUpdateMerchantTheme } from '@domain/merchants/mutations/useUpdateMerchantTheme';

const AdminActions = ({ user, isAdmin }) => {
  const queryClient = useQueryClient();
  const onError = useError();

  const { mutate, isLoading: setting } = useSetAdmin({
    onSuccess: (_, variables) => {
      const key = ['theme-auth-user', user.email];
      const prev: admin.auth.UserRecord | undefined = queryClient.getQueryData(key);
      queryClient.setQueryData(key, {
        ...prev,
        customClaims: {
          ...prev!.customClaims,
          admin: variables.setTo,
        },
      });
    },
    onError: (error) => onError({ error, toastError: true }),
  });

  const handleClick = async () => {
    const idToken = await auth().currentUser.getIdToken();
    mutate({
      idToken,
      newAdminUserIds: [user.uid],
      setTo: !isAdmin,
    });
  };

  return (
    <Button
      variant="link"
      colorScheme={isAdmin ? 'danger' : 'success'}
      onClick={handleClick}
      isLoading={setting}
    >
      {isAdmin ? 'Remove' : 'Add'} admin status
    </Button>
  );
};

const ManageAccessActions = ({ user, isOwner, isEditor }) => {
  const { data: theme } = useMerchantTheme();
  const onError = useError();

  const { mutate, isLoading } = useUpdateMerchantTheme({
    onError: (error) => onError({ error, toastError: true }),
  });

  const handleOwnerChange = () => {
    if (theme?.id) {
      mutate({
        docId: theme.id,
        theme: {
          owners: isOwner ? FieldValue?.arrayRemove(user.uid) : FieldValue.arrayUnion(user.uid),
        },
      });
    }
  };

  const handleEditorChange = () => {
    if (theme?.id) {
      mutate({
        docId: theme.id,
        theme: {
          editors: isEditor ? FieldValue?.arrayRemove(user.uid) : FieldValue.arrayUnion(user.uid),
        },
      });
    }
  };

  return (
    <ButtonGroup variant="link">
      <Button
        colorScheme={isOwner ? 'danger' : 'warning'}
        onClick={handleOwnerChange}
        isLoading={isLoading}
      >
        {isOwner ? 'Remove' : 'Add'} as owner
      </Button>
      <Button
        colorScheme={isEditor ? 'danger' : 'primary'}
        onClick={handleEditorChange}
        isLoading={isLoading}
      >
        {isEditor ? 'Remove' : 'Add'} as editor
      </Button>
    </ButtonGroup>
  );
};

type TThemeUserProps = {
  email?: string;
  uid?: string;
};

export const ThemeUser = ({ email, uid }: TThemeUserProps) => {
  const { data: emailUser, isLoading: emailLoading, error: emailError } = useGetUserByEmail(email!);
  const { data: uidUser, isLoading: uidLoading, error: uidError } = useGetUserByUid(uid!);
  const { view } = useCustomThemeDraftState();

  const user = uidUser || emailUser;

  const { isOwner, isEditor } = useThemeRoles(user?.uid || 'fake-id');

  const isAdmin = user?.customClaims?.admin;
  const error = uidError || emailError;

  if (emailLoading || uidLoading) return <Skeleton w="100%" h={14} />;
  if (error || !user)
    return <Banner status="error" error={error} title="User not found" description={email} />;

  return (
    <HStack align="flex-start" w="100%" spacing={6}>
      <VStack>
        <Avatar src={user?.photoURL} name={user?.displayName || user?.email} />
        {isAdmin && <Tag colorScheme="danger">admin</Tag>}
        {isOwner && <Tag colorScheme="warning">owner</Tag>}
        {isEditor && <Tag colorScheme="success">editor</Tag>}
      </VStack>
      <VStack align="flex-start">
        <Flex flexDir="column">
          <Text textStyle="subtitle1">{user?.displayName ?? 'No display name'}</Text>
          <Text>{user?.email}</Text>
        </Flex>
        {view === ECustomThemeViews.ADMIN ? (
          <AdminActions user={user} isAdmin={isAdmin} />
        ) : (
          <ManageAccessActions user={user} isOwner={isOwner} isEditor={isEditor} />
        )}
      </VStack>
    </HStack>
  );
};
